import React, { memo } from 'react';

import orderBy from 'lodash.orderby';
import type { FieldValues } from 'react-hook-form';

import type { QuickAddSectionProps, QuickItemRenderer } from '@/_containers';
import { AsyncQuickAddSection, phoneNumberIncludeKey } from '@/_containers';
import type { TeamMemberQueryLinkerWithContactInfoSchema } from '@/_schemas/team-members/team-members-query-linker-with-contact-info-schema';
import { nameResolver } from '@/_shared';

import { ItemWithPhoneNumber } from './item-with-phone-number';

import { useGetTeamMembersMutation } from '../../apis';

type ContactMultiSelectProps<TFormValues extends FieldValues = FieldValues> = Pick<
  QuickAddSectionProps<TeamMemberQueryLinkerWithContactInfoSchema, TFormValues>,
  'isEditable' | 'control' | 'fieldName' | 'title' | 'sectionTitle' | 'icon' | 'rightSlot' | 'paginatedItems'
> & {
  contactId?: string | number;
  items: TeamMemberQueryLinkerWithContactInfoSchema[];
  showHeader?: boolean;
  showPrimaryContactAction?: boolean;
  hasPrimary?: boolean;
  primaryContactsMap?: Record<number, TeamMemberQueryLinkerWithContactInfoSchema>;
  setContactType?: (item: TeamMemberQueryLinkerWithContactInfoSchema, isPrimary: boolean) => void;
  emptyDataMessage?: string;
  sortable?: boolean;
};

const apiParams = { include: `${phoneNumberIncludeKey()},profile_image` };

function AsyncTeamMembersMultiSelectWithPhoneNumberBase<TFormValues extends FieldValues = FieldValues>({
  contactId,
  title = 'Contacts',
  sectionTitle = 'Contacts',
  items = [],
  showPrimaryContactAction = false,
  hasPrimary = false,
  primaryContactsMap = {},
  setContactType,
  emptyDataMessage = 'No Team Members',
  sortable = false,
  ...rest
}: ContactMultiSelectProps<TFormValues>) {
  const renderItem: QuickItemRenderer<TeamMemberQueryLinkerWithContactInfoSchema> = React.useCallback(
    (item, { isEditable, removeItem }) => (
      <ItemWithPhoneNumber
        key={item.id}
        item={item}
        isEditable={isEditable}
        sortable={sortable}
        removeItemHandler={removeItem}
        showPrimaryContactAction={showPrimaryContactAction}
        isPrimary={!!primaryContactsMap[item.id]}
        hasPrimary={hasPrimary}
        setContactType={setContactType}
      />
    ),
    [sortable, showPrimaryContactAction, primaryContactsMap, hasPrimary, setContactType]
  );

  const itemToString = React.useCallback(
    (item: TeamMemberQueryLinkerWithContactInfoSchema | null) =>
      item ? nameResolver(item.first_name, item.last_name) : '',
    []
  );

  const keyExtractor = React.useCallback((item: TeamMemberQueryLinkerWithContactInfoSchema) => item.id.toString(), []);

  const sortValues = React.useCallback(
    // TO SEE THE DIFFERENCE, CHANGE 'name' TO 'itemToString'
    (values: TeamMemberQueryLinkerWithContactInfoSchema[]) => orderBy(values, itemToString),
    [itemToString]
  );

  const filterOptions = React.useCallback(
    (item: TeamMemberQueryLinkerWithContactInfoSchema) => (contactId ? item.id === Number(contactId) : false),
    [contactId]
  );

  const sortedItems = React.useMemo(() => (sortable ? items : sortValues(items)), [items, sortValues, sortable]);

  return (
    <AsyncQuickAddSection
      {...rest}
      sectionTitle={sectionTitle}
      title={title}
      emptyDataMessage={emptyDataMessage}
      items={sortedItems}
      keyExtractor={keyExtractor}
      itemToString={itemToString}
      renderItem={renderItem}
      useApi={useGetTeamMembersMutation}
      sortValues={sortable ? undefined : sortValues}
      filterOptions={filterOptions}
      sortable={sortable}
      apiParams={apiParams}
    />
  );
}

export const AsyncTeamMembersMultiSelectWithPhoneNumber = memo(
  AsyncTeamMembersMultiSelectWithPhoneNumberBase
) as typeof AsyncTeamMembersMultiSelectWithPhoneNumberBase;
